// Settings Categories Configuration
// This file contains all available settings categories with their UI configuration

import React from 'react';
import { GeneralSettingsPage } from '../pages/GeneralSettings/GeneralSettingsPage';
import { PrivacySecuritySettingsPage } from '../pages/PrivacySecuritySettings/PrivacySecuritySettingsPage';
import { KeyboardSettingsPage } from '../pages/KeyboardSettings/KeyboardSettingsPage';
import { DisplaySettingsPage } from '../pages/DisplaySettings/DisplaySettingsPage';
import { SoundAndCameraSettingsPage } from '../pages/SoundAndCameraSettings/SoundAndCameraSettingsPage';
import { DevicesSettingsPage } from '../pages/DevicesSettings/DevicesSettingsPage';
import { MouseSettingsPage } from '../pages/MouseSettings/MouseSettingsPage';
import { AccountManagementPage } from '../pages/AccountManagement/AccountManagementPage';
import { CollectLogsPage } from '../pages/SupportAndTroubleshoot/CollectLogsPage/CollectLogsPage';
import { SendFeedbackPage } from '../pages/SupportAndTroubleshoot/SendFeedbackPage/SendFeedbackPage';
import { CheckConfigurationPage } from '../pages/SupportAndTroubleshoot/CheckConfigurationPage/CheckConfigurationPage';
import { IconProps } from '@citrite/web-ui-component';
import { SettingsCategory as SettingsCategoryEnum } from './SettingsCategory';
import {
	DisplayIcon,
	GeneralIcon,
	KeyboardIcon,
	PrivacySecurityIcon,
	SoundAndCameraIcon,
	DevicesIcon,
	MouseIcon,
	AccountManagementIcon,
	AddStoreIcon,
	CollectLogsIcon,
	SendFeedbackIcon,
	CheckConfigurationIcon,
	ResetCWAIcon,
	HelpDocumentationIcon,
} from '../Icons/SettingsCategoryIcons/SettingsCategoryIcons';
import { SETTINGS_CATEGORY_DESCRIPTIONS, SettingsCategory } from './SettingsCategory';

export interface ISettingCategory {
	id: string;
	title: string;
	description: string;
	icon: React.FC<IconProps>;
	component: React.ComponentType<{
		title: string;
		description?: string;
		categoryId?: string;
		onNavigate?: (category: SettingsCategoryEnum) => void;
	}>;
	order: number;
	isEnabled?: boolean;
	route?: string;
}

export const SettingsCategories: ISettingCategory[] = [
	// General - Available on all platforms and all entry types
	{
		id: SettingsCategory.General,
		title: SettingsCategory.General,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.General],
		icon: GeneralIcon,
		component: GeneralSettingsPage,
		order: 1,
		isEnabled: true,
		route: '/general',
	},
	// Keyboard - Windows and Linux, Endpoint and InSession only
	{
		id: SettingsCategory.Keyboard,
		title: SettingsCategory.Keyboard,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.Keyboard],
		icon: KeyboardIcon,
		component: KeyboardSettingsPage,
		order: 4,
		isEnabled: true,
		route: '/keyboard',
	},
	// Display - Windows and Linux, Endpoint and InSession only
	{
		id: SettingsCategory.Display,
		title: SettingsCategory.Display,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.Display],
		icon: DisplayIcon,
		component: DisplaySettingsPage,
		order: 6,
		isEnabled: true,
		route: '/display',
	},
	// Privacy and Security - Available on all platforms, Endpoint and AccountManagement only
	{
		id: SettingsCategory.PrivacySecurity,
		title: SettingsCategory.PrivacySecurity,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.PrivacySecurity],
		icon: PrivacySecurityIcon,
		component: PrivacySecuritySettingsPage,
		order: 7,
		isEnabled: true,
		route: '/privacy-security',
	},
	// Sound and Camera - Mac only, Endpoint
	{
		id: SettingsCategory.SoundAndCamera,
		title: SettingsCategory.SoundAndCamera,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.SoundAndCamera],
		icon: SoundAndCameraIcon,
		component: SoundAndCameraSettingsPage,
		order: 2,
		isEnabled: true,
		route: '/sound-camera',
	},
	// Devices - Mac only, Endpoint
	{
		id: SettingsCategory.Devices,
		title: SettingsCategory.Devices,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.Devices],
		icon: DevicesIcon,
		component: DevicesSettingsPage,
		order: 5,
		isEnabled: true,
		route: '/devices',
	},
	// Mouse - Mac only, Endpoint
	{
		id: SettingsCategory.Mouse,
		title: SettingsCategory.Mouse,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.Mouse],
		icon: MouseIcon,
		component: MouseSettingsPage,
		order: 3,
		isEnabled: true,
		route: '/mouse',
	},
	// Account Management - Stores list
	{
		id: SettingsCategory.AccountManagement,
		title: 'Stores',
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.AccountManagement],
		icon: AccountManagementIcon,
		component: AccountManagementPage,
		order: 0,
		isEnabled: true,
		route: '/stores',
	},
	// Add Store - trigger item in sidebar; clicking fires setSetting('account-add-store', true)
	// to instruct native to launch the Add Account flow (e.g. selfservice.exe --addAccount).
	// AccountManagementPage detects title === 'Add store', fires the trigger, then navigates back.
	{
		id: SettingsCategory.AddStore,
		title: 'Add store',
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.AddStore],
		icon: AddStoreIcon,
		component: AccountManagementPage,
		order: 1,
		isEnabled: true,
		route: '/add-store',
	},
	// Support and Troubleshoot categories
	{
		id: SettingsCategory.CollectLogs,
		title: SettingsCategory.CollectLogs,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.CollectLogs],
		icon: CollectLogsIcon,
		component: CollectLogsPage,
		order: 0,
		isEnabled: true,
		route: '/collect-logs',
	},
	{
		id: SettingsCategory.SendFeedback,
		title: SettingsCategory.SendFeedback,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.SendFeedback],
		icon: SendFeedbackIcon,
		component: SendFeedbackPage,
		order: 1,
		isEnabled: true,
		route: '/send-feedback',
	},
	{
		id: SettingsCategory.CheckConfiguration,
		title: SettingsCategory.CheckConfiguration,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.CheckConfiguration],
		icon: CheckConfigurationIcon,
		component: CheckConfigurationPage,
		order: 2,
		isEnabled: true,
		route: '/check-configuration',
	},
	{
		id: SettingsCategory.ResetCWA,
		title: SettingsCategory.ResetCWA,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.ResetCWA],
		icon: ResetCWAIcon,
		component: CollectLogsPage,
		order: 3,
		isEnabled: true,
		route: '/reset-cwa',
	},
	{
		id: SettingsCategory.HelpDocumentation,
		title: SettingsCategory.HelpDocumentation,
		description: SETTINGS_CATEGORY_DESCRIPTIONS[SettingsCategory.HelpDocumentation],
		icon: HelpDocumentationIcon,
		component: CollectLogsPage,
		order: 4,
		isEnabled: true,
		route: '/help-documentation',
	},
];

/**
 * Helper function to find category by ID
 * @param categories Array of setting categories
 * @param id The category ID to find
 * @returns The category object or undefined if not found
 */
export function getCategoryById(
	categories: ISettingCategory[],
	id: SettingsCategory
): ISettingCategory | undefined {
	return categories.find((category) => category.id === id);
}
